import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { db } from "../firebase.js";
import { auth } from "../firebase.js";
import { ref, onValue, set, update, remove } from "firebase/database";
import { onAuthStateChanged } from "firebase/auth";

let unsubscribe = null;
let authUnsubscribe = null;

// ─── Real-time listener ───────────────────────────────────────────────────────
export const subscribeOrders = (userId) => (dispatch) => {
  if (!db) return;

  const startListener = (uid) => {
    if (unsubscribe) unsubscribe();
    if (!uid) { dispatch(setOrders([])); return; }

    unsubscribe = onValue(
      ref(db, "orders"),
      (snapshot) => {
        if (!snapshot.exists()) { dispatch(setOrders([])); return; }
        const val = snapshot.val();
        let list = Object.keys(val).map((k) => ({ id: k, ...val[k] }));
        if (userId) list = list.filter((o) => o.userId === userId);
        dispatch(setOrders(list));
      },
      (error) => console.error("❌ Orders real-time error:", error.message)
    );
  };

  // Wait for auth to be ready before subscribing
  if (auth?.currentUser) {
    startListener(auth.currentUser.uid);
  } else if (auth) {
    if (authUnsubscribe) authUnsubscribe();
    authUnsubscribe = onAuthStateChanged(auth, (user) => {
      startListener(user?.uid || null);
    });
  }
};

export const fetchOrders = (userId) => subscribeOrders(userId);

// ─── Write ops ────────────────────────────────────────────────────────────────
export const createOrder = createAsyncThunk("orders/create", async (order) => {
  const orderId = order.orderId || order.id || `ORD-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
  const clean = (obj) => JSON.parse(JSON.stringify(obj, (_, v) => (v === undefined ? null : v)));
  const orderData = clean({ ...order, id: orderId, orderId, createdAt: order.createdAt || Date.now(), status: order.status || "confirmed" });
  await set(ref(db, `orders/${orderId}`), orderData);
  if (order.userId) await set(ref(db, `users/${order.userId}/orders/${orderId}`), true);
  return orderData;
});

export const updateOrder = createAsyncThunk("orders/update", async ({ id, data }) => {
  await update(ref(db, `orders/${id}`), { ...data, updatedAt: Date.now() });
  return { id, ...data };
});

export const deleteOrder = createAsyncThunk("orders/delete", async (id) => {
  await remove(ref(db, `orders/${id}`));
  return id;
});

// ─── Slice ────────────────────────────────────────────────────────────────────
const ordersSlice = createSlice({
  name: "orders",
  initialState: { items: [], status: "idle", error: null },
  reducers: {
    setOrders(state, action) {
      state.items = action.payload;
      state.status = "succeeded";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createOrder.fulfilled, (state, action) => {
        const exists = state.items.find((o) => o.id === action.payload.id);
        if (!exists) state.items.push(action.payload);
        state.status = "succeeded";
      })
      .addCase(updateOrder.fulfilled, (state, action) => {
        const idx = state.items.findIndex((o) => o.id === action.payload.id);
        if (idx !== -1) state.items[idx] = { ...state.items[idx], ...action.payload };
        state.status = "succeeded";
      })
      .addCase(deleteOrder.fulfilled, (state, action) => {
        state.items = state.items.filter((o) => o.id !== action.payload);
      });
  },
});

export const { setOrders } = ordersSlice.actions;
export default ordersSlice.reducer;
